import { Routes, Route, Navigate } from 'react-router-dom';
import { type ReactNode } from 'react';
import { useAuth } from './context/AuthContext';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import BrokerMomentsPage from './pages/BrokerMomentsPage';

/** Minimal full-screen loading state shown while the session is being verified. */
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">
      <p className="text-sm">Loading...</p>
    </div>
  );
}

/**
 * Protected route gate.
 *
 * Honors the AuthContext contract (Wisdom §4): it NEVER decides access until
 * the on-mount verify() against /api/auth/me has resolved — i.e. while
 * isLoading is true it renders the loading screen, not the protected child and
 * not a redirect. Only once isLoading clears does it trust isAuthenticated.
 * This is what stops a stale cached (optimistic) user from being granted access.
 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected — broker moments */}
      <Route
        path="/broker-moments"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BrokerMomentsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Unknown paths fall back to the (protected) root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
